import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';
// assets
import devicesIconLight from 'assets/images/icons/devices-icon.png';
import devicesIconDark from 'assets/images/icons/devices-icon-dark.png';
import networkManageIconLight from 'assets/images/icons/network-manage.png';
import networkManageIconDark from 'assets/images/icons/network-manage-dark.png';
import {
  IconKey,
  IconHome,
  IconBuildings,
  IconRouter,
  IconDevices2,
  IconSitemap,
  IconShieldHalf,
  IconUserSquare,
  IconCalendarFilled,
  IconUserCircle,
  IconMapPin,
  IconDeviceDesktop,
  IconBell,
  IconCalendarEvent,
  IconBroadcast,
  IconSparkles,
  IconRadar
} from '@tabler/icons-react';
import { FEATURE_IDS } from 'utils/supportedFeatures';
import roleManagement from 'utils/roleManagement';

const isReadOnlyManager = () => roleManagement.getUserRole() === 'roManager';
const isManager = () => {
  const role = roleManagement.getUserRole();
  return role === 'roManager' || role === 'rwManager';
};
// constant
const icons = {
  IconRadar,
  IconKey,
  IconHome,
  IconBuildings,
  IconRouter,
  IconDevices2,
  IconSitemap,
  IconShieldHalf,
  IconUserSquare,
  IconCalendarFilled,
  IconUserCircle,
  IconMapPin,
  IconDeviceDesktop,
  IconBell,
  IconCalendarEvent,
  IconBroadcast,
  IconSparkles
};

const ThemedImageIcon = ({ lightSrc, darkSrc, alt, width = 18, height = 18 }) => {
  const { palette } = useTheme();
  const src = palette.mode === 'dark' ? darkSrc : lightSrc;

  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      sx={{
        width,
        height
      }}
    />
  );
};

const DeviceIcon = () => <ThemedImageIcon lightSrc={devicesIconLight} darkSrc={devicesIconDark} alt="Devices" width={18} height={11} />;

const NetworkManageIcon = () => (
  <ThemedImageIcon lightSrc={networkManageIconLight} darkSrc={networkManageIconDark} alt="Network Manage" width={18} height={18} />
);

// Helper to resolve the current organization name dynamically at runtime
const getCurrentOrgName = () => {
  try {
    const savedOrgName = typeof localStorage !== 'undefined' ? localStorage.getItem('savedOrgName') : null;
    if (savedOrgName) return savedOrgName;

    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const parts = path.split('/');
    const orgIdx = parts.indexOf('organization');
    if (orgIdx !== -1 && parts.length > orgIdx + 1) {
      try {
        return decodeURIComponent(parts[orgIdx + 1]);
      } catch (e) {
        return parts[orgIdx + 1];
      }
    }

    const selectedTypeRaw = typeof localStorage !== 'undefined' ? localStorage.getItem('selectedType') : null;
    if (selectedTypeRaw) {
      try {
        const selectedType = JSON.parse(selectedTypeRaw);
        if (selectedType && selectedType.label) return selectedType.label;
      } catch (e) {
        // ignore JSON parse errors
      }
    }
  } catch (e) {
    // Accessing window/localStorage might fail in uncommon environments
  }
  return '';
};

const separateMenuNavBar = {
  id: 'Organization Menu',
  title: 'Organization Menu',
  caption: '',
  icon: icons.IconKey,
  type: 'group',
  children: [
    {
      id: 'Dashboard-mspLevel',
      title: 'Dashboard',
      type: 'item',
      icon: icons.IconDeviceDesktop,
      url: '/mspHome/dashboard',
      target: false
    },
    {
      id: 'Organizations-mspLevel',
      title: 'Organizations',
      type: 'item',
      icon: icons.IconBuildings,
      url: '/mspHome/organizations',
      target: false
    },
    {
      id: 'Locations-mspLevel',
      title: 'Locations',
      type: 'item',
      icon: icons.IconMapPin,
      url: '/mspHome/locations',
      target: false
    },
    {
      id: 'Devices-mspLevel',
      title: 'Devices',
      type: 'item',
      icon: DeviceIcon,
      url: '/mspHome/devices',
      target: false
    },
    {
      id: 'Administration-mspLevel',
      title: 'Administration',
      type: 'collapse',
      icon: icons.IconUserSquare,
      url: '/mspHome/administration',
      target: false,
      flyout: true,
      children: [
        {
          id: 'AdministrationUsers-mspLevel',
          title: 'users',
          type: 'item',
          hideIcon: true,
          url: '/mspHome/administration/users',
          target: false,
          get hidden() {
            return isManager();
          }
        },
        {
          id: 'AdministrationManageSubscriptions-mspLevel',
          title: 'manageSubscriptions',
          type: 'item',
          hideIcon: true,
          url: '/mspHome/administration/manage-subscriptions',
          target: false
        },
        {
          id: 'AdministrationCloudRestApiTop-mspLevel',
          title: 'cloudRestApi',
          type: 'item',
          hideIcon: true,
          url: '/mspHome/administration/cloud-rest-api',
          target: false,
          cloudRestApiGate: true
        },
        {
          id: 'AdministrationReports-mspLevel',
          title: 'reports',
          type: 'item',
          hideIcon: true,
          url: '/mspHome/administration/reports',
          target: false,
          featureGate: FEATURE_IDS.REPORTS,
          get hidden() {
            return isReadOnlyManager();
          }
        },
      ]
    },
    {
      id: 'Alarms-mspLevel',
      title: 'Alarms',
      type: 'item',
      icon: icons.IconBell,
      url: '/mspHome/notifications',
      target: false
    },
    {
      id: 'Hindsight-mspLevel',
      title: 'Hindsight',
      type: 'item',
      icon: icons.IconRadar,
      url: '/hindsight',
      featureGate: FEATURE_IDS.HINDSIGHT_ENABLED,
      target: false
    },
    // Dynamic org tabs (computed at render time)
    {
      id: 'DashboardOrg-parOrg',
      title: 'Dashboard',
      type: 'item',
      icon: icons.IconDeviceDesktop,
      get link() {
        const orgName = getCurrentOrgName();
        return `/organization/${encodeURIComponent(orgName)}/dashboard`;
      },
      get url() {
        return this.link;
      },
      target: false
    },
    {
      id: 'LocationsOrg-parOrg',
      title: 'Locations',
      type: 'item',
      icon: icons.IconMapPin,
      get link() {
        const orgName = getCurrentOrgName();
        return `/organization/${encodeURIComponent(orgName)}/locations`;
      },
      get url() {
        return this.link;
      },
      target: false
    },
    {
      id: 'DevicesOrg-parOrg',
      title: 'Devices',
      type: 'item',
      icon: DeviceIcon,
      get link() {
        const orgName = getCurrentOrgName();
        return `/organization/${encodeURIComponent(orgName)}/devices`;
      },
      get url() {
        return this.link;
      },
      target: false
    },
    {
      id: 'SecurityCenterOrg-parOrg',
      title: 'securityCenter',
      type: 'item',
      icon: icons.IconShieldHalf,
      get link() {
        const orgName = getCurrentOrgName();
        return `/organization/${encodeURIComponent(orgName)}/security-center`;
      },
      get url() {
        return this.link;
      },
      target: false
    },
    {
      id: 'ClientsOrg-parOrg',
      title: 'Clients',
      type: 'item',
      icon: icons.IconSitemap,
      get link() {
        const orgName = getCurrentOrgName();
        return `/organization/${encodeURIComponent(orgName)}/clients`;
      },
      get url() {
        return this.link;
      },
      target: false
    },
    {
      id: 'SSIDOrg-parOrg',
      title: 'SSID',
      type: 'item',
      icon: icons.IconUserCircle,
      get link() {
        const orgName = getCurrentOrgName();
        return `/organization/${encodeURIComponent(orgName)}/SSID`;
      },
      get url() {
        return this.link;
      },
      target: false
    },
    {
      id: 'AdministrationOrg-parOrg',
      title: 'Administration',
      type: 'collapse',
      icon: icons.IconUserSquare,
      get link() {
        const orgName = getCurrentOrgName();
        return `/organization/${encodeURIComponent(orgName)}/administration`;
      },
      get url() {
        return this.link;
      },
      target: false,
      flyout: true,
      children: [
        {
          id: 'AdministrationUsersOrg-parOrg',
          title: 'users',
          type: 'item',
          hideIcon: true,
          get link() {
            const orgName = getCurrentOrgName();
            return `/organization/${encodeURIComponent(orgName)}/administration/users`;
          },
          get url() {
            return this.link;
          },
          target: false,
          get hidden() {
            return isManager();
          }
        },
        {
          id: 'AdministrationPolicyOrg-parOrg',
          title: 'policy',
          type: 'item',
          hideIcon: true,
          get link() {
            const orgName = getCurrentOrgName();
            return `/organization/${encodeURIComponent(orgName)}/administration/policy`;
          },
          get url() {
            return this.link;
          },
          target: false
        },
        {
          id: 'AdministrationManageSubscriptionsOrg-parOrg',
          title: 'manageSubscriptions',
          type: 'item',
          hideIcon: true,
          get link() {
            return `/mspHome/administration/manage-subscriptions`;
          },
          get url() {
            return this.link;
          },
          target: false
        },
        {
          id: 'AdministrationCloudRestApiOrg-parOrg',
          title: 'cloudRestApi',
          type: 'item',
          hideIcon: true,
          get link() {
            return `/mspHome/administration/cloud-rest-api`;
          },
          get url() {
            return this.link;
          },
          target: false,
          cloudRestApiGate: true
        },
        {
          id: 'AdministrationReportsOrg-parOrg',
          title: 'reports',
          type: 'item',
          hideIcon: true,
          get link() {
            const orgName = getCurrentOrgName();
            return `/organization/${encodeURIComponent(orgName)}/administration/reports`;
          },
          get url() {
            return this.link;
          },
          target: false,
          featureGate: FEATURE_IDS.REPORTS,
          get hidden() {
            return isReadOnlyManager();
          }
        }
      ]
    },
    {
      id: 'AlarmsOrg-parOrg',
      title: 'Alarms',
      type: 'item',
      icon: icons.IconBell,
      get link() {
        const orgName = getCurrentOrgName();
        return `/organization/${encodeURIComponent(orgName)}/notifications`;
      },
      get url() {
        return this.link;
      },
      target: false
    },
    {
      id: 'Hindsight-parOrg',
      title: 'Hindsight',
      type: 'item',
      icon: icons.IconRadar,
      url: '/hindsight',
      featureGate: FEATURE_IDS.HINDSIGHT_ENABLED,
      target: false
    },
    {
      id: 'ScheduledOrg-parOrg',
      title: 'rebootScheduleMenu',
      type: 'item',
      icon: icons.IconCalendarEvent,
      get link() {
        const orgName = getCurrentOrgName();
        return `/organization/${encodeURIComponent(orgName)}/scheduled`;
      },
      get url() {
        return this.link;
      },
      target: false,
      featureGate: FEATURE_IDS.REBOOT_SCHEDULE
    },
    {
      id: 'LocDashboard-loc',
      title: 'Dashboard',
      type: 'item',
      icon: icons.IconDeviceDesktop,
      url: '/dashboard',
      target: false
    },
    {
      // AIDN beta — visible at the location/network level. NavGroup filters
      // children with id-suffix `-loc` when selectedType.type === "Loc".
      // The route lands on /aidn (see MainRoutes.jsx). AIDN's three flags were
      // consolidated into one: the nav entry is hidden unless AIDN_MASTER_ENABLED
      // (featureConf, resolved via the supported-feature list), and the page itself
      // falls back to the disabled splash on the same flag via useAidnFeatureGate.
      id: 'LocAidn-loc',
      title: 'AI Defined Network',
      caption: 'beta',
      type: 'item',
      icon: icons.IconSparkles,
      url: '/aidn',
      featureGate: [FEATURE_IDS.AIDN_MASTER_ENABLED],
      target: false
    },
    {
      id: 'LocDevices-loc',
      title: 'Devices',
      type: 'item',
      icon: DeviceIcon,
      url: '/locationDevice',
      target: false
    },
    {
      id: 'LocClients-loc',
      title: 'Clients',
      type: 'item',
      icon: icons.IconSitemap,
      url: '/clients',
      target: false
    },
    {
      id: 'LocWiFi-loc',
      title: 'Wi-Fi',
      type: 'collapse',
      icon: icons.IconRouter,
      url: '/Wi-Fi',
      target: false,
      flyout: true,
      children: [
        {
          id: 'LocWiFiSSID-loc',
          title: 'SSID',
          type: 'item',
          hideIcon: true,
          url: '/Wi-Fi/SSID',
          target: false
        },
        {
          id: 'LocWiFiProfiles-loc',
          title: 'profiles',
          type: 'item',
          hideIcon: true,
          url: '/Wi-Fi/Profiles',
          target: false
        },
        {
          id: 'LocWiFiSchedules-loc',
          title: 'wifiScheduleMenu',
          type: 'item',
          hideIcon: true,
          url: '/Wi-Fi/schedules',
          target: false
        },
        {
          id: 'LocWiFiInstantWiFi-loc',
          title: 'instantWiFi',
          type: 'item',
          hideIcon: true,
          url: '/Wi-Fi/instantWiFi',
          target: false
        },
        {
          id: 'LocWiFiLoadBalancing-loc',
          title: 'Load Balancing',
          type: 'item',
          hideIcon: true,
          url: '/Wi-Fi/LoadBalancing',
          target: false
        },
        {
          id: 'LocWiFiManagementVlan-loc',
          title: 'managementVlanSelectLabel',
          type: 'item',
          hideIcon: true,
          url: '/Wi-Fi/ManagementVLAN',
          target: false
        },
        {
          id: 'LocWiFiAdvance-loc',
          title: 'advanced',
          type: 'collapse',
          hideIcon: true,
          flyout: true,
          url: '/Wi-Fi/Advance',
          target: false,
          children: [
            {
              id: 'LocWiFiAdvanceFastRoaming-loc',
              title: 'fastRoaming',
              type: 'item',
              hideIcon: true,
              url: '/Wi-Fi/Advance/FastRoaming',
              target: false
            },
            {
              id: 'LocWiFiAdvanceNetwork-loc',
              title: 'networkSettings',
              type: 'item',
              hideIcon: true,
              url: '/Wi-Fi/Advance/Network-Settings',
              target: false
            },
            {
              id: 'LocWiFiAdvanceMeshNetworkSettings-loc',
              title: 'meshSettings',
              type: 'item',
              hideIcon: true,
              url: '/Wi-Fi/Advance/MeshNetworkSettings',
              target: false
            },
            {
              id: 'multiPSKSettings-loc',
              title: 'mpskSettingsTitle',
              type: 'item',
              hideIcon: true,
              url: '/Wi-Fi/Advance/MultiPSKSettings',
              target: false
            },
            {
              id: 'LocWiFiAdvanceTrafficPolicies-loc',
              title: 'trafficPolicies',
              type: 'item',
              hideIcon: true,
              url: '/Wi-Fi/Advance/TrafficPolicies',
              target: false
            },
            {
              id: 'LocWiFiAdvanceURLFiltering-loc',
              title: 'urlFiltering',
              type: 'item',
              hideIcon: true,
              url: '/Wi-Fi/Advance/URLFiltering',
              target: false
            },
            {
              id: 'LocWiFiAdvanceWireless-loc',
              title: 'wirelessSettings',
              type: 'item',
              hideIcon: true,
              url: '/Wi-Fi/Advance/WirelessSettings',
              target: false
            }
          ]
        }
      ]
    },
    {
      id: 'LocWired-loc',
      title: 'Wired',
      type: 'collapse',
      icon: icons.IconDevices2,
      url: '/wired',
      target: false,
      flyout: true,
      children: [
        {
          id: 'LocVLANSetup-loc',
          title: 'VLAN/Network Setup',
          type: 'item',
          hideIcon: true,
          url: '/wired/VLAN',
          target: false
        },
        {
          id: 'LocLag-loc',
          title: 'lag',
          type: 'item',
          hideIcon: true,
          url: '/wired/lag',
          target: false
        },
        {
          id: 'LocRadiusConfig-loc',
          title: 'radiusConfiguration',
          type: 'item',
          hideIcon: true,
          url: '/wired/radiusConfiguration',
          target: false
        },
        {
          id: 'LocSpanningTree-loc',
          title: 'spanningTree',
          type: 'item',
          hideIcon: true,
          url: '/wired/spanningTree',
          target: false
        },
        {
          id: 'LocWiredManagementVlan-loc',
          title: 'managementVlanSelectLabel',
          type: 'item',
          hideIcon: true,
          url: '/wired/managementVlan',
          target: false,
          featureGate: FEATURE_IDS.WIRED_MANAGEMENT_VLAN
        },
        {
          id: 'LocPoESchedules-loc',
          title: 'poeDowntimeSchedules',
          type: 'item',
          hideIcon: true,
          url: '/wired/PoESchedules',
          target: false
        },
        {
          id: 'LocDhcpRelay-loc',
          title: 'dhcpL2Relay',
          type: 'item',
          hideIcon: true,
          url: '/wired/dhcpRelay',
          target: false,
          featureGate: FEATURE_IDS.DHCP_SNOOPING_RELAY
        },
        {
          id: 'LocDhcpSnooping-loc',
          title: 'dhcpSnooping',
          type: 'item',
          hideIcon: true,
          url: '/wired/dhcpSnooping',
          target: false,
          featureGate: FEATURE_IDS.DHCP_SNOOPING_RELAY
        }
      ]
    },
    {
      id: 'LocWiFiMobileProfile-loc',
      title: 'mobileHotspot',
      type: 'item',
      icon: icons.IconBroadcast,
      url: '/MobileProfile',
      target: false
    },

    {
      id: 'LocLocationSettings-loc',
      title: 'settings',
      type: 'collapse',
      icon: NetworkManageIcon,
      url: '/locationSettings',
      target: false,
      flyout: true,
      children: [
        {
          id: 'LocLocationSettingsGeneral-loc',
          title: 'general',
          type: 'item',
          hideIcon: true,
          url: '/locationSettings/general',
          target: false
        },
        {
          id: 'LocLocationSettingsVlan-loc',
          title: 'VLAN/Network Setup',
          type: 'item',
          hideIcon: true,
          url: '/locationSettings/VLAN',
          target: false
        },
        {
          id: 'LocLocationSettingsRouting-loc',
          title: 'routing',
          type: 'item',
          hideIcon: true,
          url: '/locationSettings/routing',
          target: false
        },
        {
          id: 'LocLocationSettingsRadius-loc',
          title: 'radius',
          type: 'item',
          hideIcon: true,
          url: '/locationSettings/radius',
          target: false
        },
        {
          id: 'Locfirmware-loc',
          title: 'firmware',
          type: 'item',
          hideIcon: true,
          url: '/locationSettings/firmware',
          target: false
        },
        {
          id: 'LocLocationSettingsSnmp-loc',
          title: 'snmp',
          type: 'item',
          hideIcon: true,
          url: '/locationSettings/SNMP',
          target: false
        },
        {
          id: 'LocLocationSettingsMDNS-loc',
          title: 'mdnsGateway',
          type: 'item',
          hideIcon: true,
          url: '/locationSettings/mdnsGateway',
          target: false
        },
        {
          id: 'LocLocationSettingsSyslogConfigurations-loc',
          title: 'syslogConfiguration',
          type: 'item',
          hideIcon: true,
          url: '/locationSettings/Syslog',
          target: false
        },
        {
          id: 'LocLocationSettingsLEDSettings-loc',
          title: 'ledSettings',
          type: 'item',
          hideIcon: true,
          url: '/locationSettings/LED-Settings',
          target: false
        },
        {
          id: 'DeviceGroupsOrg-loc',
          title: 'Device Groups',
          type: 'item',
          hideIcon: true,
          url: '/locationSettings/device-Groups',
          target: false
        },
        {
          id: 'LocLocationSettingsConfigBackup-loc',
          title: 'configurationBackupRestore',
          type: 'item',
          hideIcon: true,
          url: '/locationSettings/configBackup',
          target: false
        }
      ]
    },
    // {
    //     id: 'LocSecurity-loc',
    //     title: 'Security',
    //     type: 'item',
    //     icon: icons.IconShieldHalf,
    //     url: '/security',
    //     target: false
    // },
    // {
    //     id: 'LocEvents-loc',
    //     title: 'Events',
    //     type: 'item',
    //     icon: icons.IconCalendarFilled,
    //     url: '/events',
    //     target: false
    // },
    {
      id: 'LocAdministration-loc',
      title: 'Administration',
      type: 'collapse',
      icon: icons.IconUserSquare,
      url: '/mspHome/administration',
      target: false,
      flyout: true,
      children: [
        {
          id: 'LocAdministrationUsers-loc',
          title: 'users',
          type: 'item',
          hideIcon: true,
          url: '/mspHome/administration/users',
          target: false,
          get hidden() {
            return isManager();
          }
        },
        {
          id: 'LocAdministrationManageSubscriptions-loc',
          title: 'manageSubscriptions',
          type: 'item',
          hideIcon: true,
          url: '/mspHome/administration/manage-subscriptions',
          target: false
        },
        {
          id: 'AdministrationCloudRestApi-mspLevel',
          title: 'cloudRestApi',
          type: 'item',
          hideIcon: true,
          url: '/mspHome/administration/cloud-rest-api',
          target: false,
          cloudRestApiGate: true
        }
      ]
    },
    {
      id: 'LocAlarms-loc',
      title: 'Alarms',
      type: 'item',
      icon: icons.IconBell,
      url: '/notifications',
      target: false
    },
    {
      id: 'Hindsight-loc',
      title: 'Hindsight',
      type: 'item',
      icon: icons.IconRadar,
      url: '/hindsight',
      featureGate: FEATURE_IDS.HINDSIGHT_ENABLED,
      target: false
    },
    {
      id: 'LocScheduled-loc',
      title: 'rebootScheduleMenu',
      type: 'item',
      icon: icons.IconCalendarEvent,
      url: '/scheduled',
      target: false,
      featureGate: FEATURE_IDS.REBOOT_SCHEDULE
    },
    {
      id: 'CustomGroupDevices-customgroup',
      title: 'Devices',
      type: 'item',
      icon: icons.IconRouter,
      url: '/device-group/devices',
      target: false
    },
    {
      id: 'CustomGroupWireless-customgroup',
      title: 'wirelessCommon',
      type: 'collapse',
      icon: icons.IconRouter,
      url: '/device-group/wireless',
      target: false,
      flyout: true,
      children: [
        {
          id: 'CustomGroupWiFiSSID-customgroup',
          title: 'SSID',
          type: 'item',
          hideIcon: true,
          url: '/device-group/wireless/SSID',
          target: false
        },
        {
          id: 'CustomGroupWiFiSchedules-customgroup',
          title: 'wifiScheduleMenu',
          type: 'item',
          hideIcon: true,
          url: '/device-group/wireless/schedules',
          target: false
        },
        {
          id: 'CustomGroupWiFiLoadBalancing-customgroup',
          title: 'Load Balancing',
          type: 'item',
          hideIcon: true,
          url: '/device-group/wireless/LoadBalancing',
          target: false
        },
        {
          // Hidden on ICM-eligible locations (NavItem checks GET_ICM_ELIGIBILITY for the location).
          id: 'CustomGroupWiFiInstantWiFi-customgroup',
          title: 'instantWiFi',
          type: 'item',
          hideIcon: true,
          url: '/device-group/wireless/instantWiFi',
          hideWhenIcmEligible: true,
          target: false
        },
        {
          id: 'CustomGroupWiFiManagementVLAN-customgroup',
          title: 'managementVlanSelectLabel',
          type: 'item',
          hideIcon: true,
          url: '/device-group/wireless/ManagementVLAN',
          target: false
        }
        // {
        //     id: 'CustomGroupWiFiAdvance-customgroup',
        //     title: 'Advance',
        //     type: 'collapse',
        //     hideIcon: true,
        //     flyout: true,
        //     url: '/Wi-Fi/Advance',
        //     target: false,
        //     children: [
        //         {
        //             id: 'CustomGroupWiFiAdvanceWireless-customgroup',
        //             title: 'Wireless Settings',
        //             type: 'item',
        //             hideIcon: true,
        //             url: '/device-group/wireless/Advance/WirelessSettings',
        //             target: false
        //         },
        //         {
        //             id: 'CustomGroupWiFiAdvanceNetwork-customgroup',
        //             title: 'Network Settings',
        //             type: 'item',
        //             hideIcon: true,
        //             url: '/device-group/wireless/Advance/Network-Settings',
        //             target: false
        //         },
        //         {
        //             id: 'CustomGroupWiFiAdvanceFastRoaming-customgroup',
        //             title: 'Fast Roaming',
        //             type: 'item',
        //             hideIcon: true,
        //             url: '/device-group/wireless/Advance/FastRoaming',
        //             target: false
        //         },
        //         {
        //             id: 'multiPSKSettings-customgroup',
        //             title: 'Multi PSK Settings',
        //             type: 'item',
        //             hideIcon: true,
        //             url: '/device-group/wireless/Advance/MultiPSKSettings',
        //             target: false
        //         }
        //     ]
        // },
      ]
    },
    {
      id: 'CustomGroupWired-customgroup',
      title: 'Wired',
      type: 'collapse',
      icon: icons.IconDevices2,
      url: '/device-group/wired',
      target: false,
      flyout: true,
      children: [
        {
          id: 'CustomGroupVLANNetworkSetup-customgroup',
          title: 'VLAN/Network Setup',
          type: 'item',
          hideIcon: true,
          url: '/device-group/wired/VLAN',
          target: false
        }
      ]
    },
    {
      id: 'CustomGroupSettings-customgroup',
      title: 'settings',
      type: 'item',
      icon: icons.IconDeviceDesktop,
      url: '/device-group/settings',
      target: false
    }
  ]
};

export default separateMenuNavBar;
